import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Sun, Droplet, Zap, ArrowRight } from 'lucide-react';

const UCIrvine: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "UC Irvine";

    return (
        <div className="min-h-screen bg-white font-sans text-[#0064a4] selection:bg-[#ffd200] selection:text-[#0064a4]">
            {/* Top Bar - Blue */}
            <div className="bg-[#0064a4] text-white py-1 px-6 flex justify-end gap-6 text-[11px] font-bold uppercase tracking-wider">
                <a href="#" className="hover:text-[#ffd200]">Alumni</a>
                <a href="#" className="hover:text-[#ffd200]">Visitors</a>
                <a href="#" className="hover:text-[#ffd200]">Giving</a>
                <a href="#" className="hover:text-[#ffd200]">Directory</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b-4 border-[#ffd200]">
                <div className="max-w-[1240px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="font-sans text-3xl font-black text-[#0064a4] uppercase tracking-tighter leading-none">{schoolName}</span>
                        <span className="font-sans text-xs font-bold text-[#0064a4] uppercase tracking-widest leading-none">{schoolName}</span>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#0064a4] text-sm uppercase tracking-wide">
                         <a href="#" className="hover:text-[#ffd200] transition-colors">About</a>
                         <a href="#" className="hover:text-[#ffd200] transition-colors">Academics</a>
                         <a href="#" className="hover:text-[#ffd200] transition-colors">Admissions</a>
                         <a href="#" className="hover:text-[#ffd200] transition-colors">Research</a>
                         <a href="#" className="hover:text-[#ffd200] transition-colors">Health</a>
                         <a href="#" className="hover:text-[#ffd200] transition-colors">Campus Life</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Search className="cursor-pointer text-[#0064a4] hover:text-[#ffd200]" size={22} />
                         <Menu className="cursor-pointer lg:hidden text-[#0064a4]" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Bright Future */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1595183495804-0ee0c476722d?q=80&w=2070&auto=format&fit=crop" 
                    alt="Aldrich Park" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Blue Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#0064a4]/90 via-[#0064a4]/40 to-transparent"></div>

                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-2xl text-white">
                         <h1 className="text-5xl md:text-8xl font-black mb-6 uppercase tracking-tight">
                             Bright Past. <br/> Brilliant Future.
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-medium text-blue-100">
                             We are casting a light on the world's toughest challenges.
                         </p>
                         <Button className="bg-[#ffd200] hover:bg-[#e6bd00] text-[#0064a4] font-black uppercase tracking-widest px-10 h-14 rounded-full shadow-lg">
                             Explore {schoolName.split(' ')[0]}
                         </Button>
                     </div>
                </div>
            </header>

            {/* Stats - Blue & Gold */}
            <section className="bg-[#0064a4] py-20 px-6 md:px-12 text-white">
                <div className="max-w-[1240px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div className="p-8 border-2 border-[#ffd200] rounded-lg bg-[#00528a]">
                         <Sun size={48} className="text-[#ffd200] mx-auto mb-6" />
                         <h3 className="text-3xl font-black mb-2">#1</h3>
                         <p className="font-bold uppercase text-xs tracking-widest text-[#ffd200]">Coolest School (Sierra Club)</p>
                     </div>
                      <div className="p-8 border-2 border-[#ffd200] rounded-lg bg-[#00528a]">
                         <Droplet size={48} className="text-[#ffd200] mx-auto mb-6" />
                         <h3 className="text-3xl font-black mb-2">Sustainable</h3>
                         <p className="font-bold uppercase text-xs tracking-widest text-[#ffd200]">Leader in Water Research</p>
                     </div>
                      <div className="p-8 border-2 border-[#ffd200] rounded-lg bg-[#00528a]">
                         <Zap size={48} className="text-[#ffd200] mx-auto mb-6" />
                         <h3 className="text-3xl font-black mb-2">Innovation</h3>
                         <p className="font-bold uppercase text-xs tracking-widest text-[#ffd200]">$500M+ Research Funding</p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="py-24 px-6 md:px-12">
                 <div className="max-w-[1240px] mx-auto">
                     <div className="flex justify-between items-end mb-12 border-b-2 border-[#0064a4] pb-4">
                         <h2 className="text-3xl font-black text-[#0064a4] uppercase tracking-tight">{schoolName} News</h2>
                         <a href="#" className="font-bold text-[#0064a4] text-xs uppercase hover:underline mb-2">View Archive</a>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                         {[
                             "UCI researchers identify immune cells that help memory",
                             "New art museum receives major donation from alumni",
                             "Anteater baseball team heads to College World Series",
                             "School of Law ranked among top in the nation"
                         ].map((title, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <div className="h-48 bg-gray-100 mb-4 overflow-hidden rounded-lg relative">
                                      <div className="absolute top-2 left-2 bg-[#ffd200] text-[#0064a4] text-[10px] font-black px-2 py-1 uppercase rounded-sm z-10">Featured</div>
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+80}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="News"/>
                                 </div>
                                 <h3 className="text-lg font-bold text-[#333] group-hover:text-[#0064a4] transition-colors mb-2 leading-snug">{title}</h3>
                                  <div className="flex items-center gap-2 text-[#0064a4] font-bold text-xs uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                                     Read <ArrowRight size={12}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#1a1a1a] text-gray-400 py-16 px-6 md:px-12 border-t-8 border-[#ffd200]">
                  <div className="max-w-[1240px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <span className="font-sans font-black text-3xl text-white uppercase tracking-tight mb-2 block">{schoolName}</span>
                           <span className="text-white font-bold block mb-6">{schoolName}</span>
                          <p className="mb-6">
                              Irvine, CA 92697 <br/>
                              949-824-5011
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold uppercase text-xs tracking-widest">
                          <ul className="space-y-4 hover:[&_a]:text-[#ffd200]">
                              <li><a href="#">Emergency</a></li>
                              <li><a href="#">Policies</a></li>
                              <li><a href="#">Privacy</a></li>
                              <li><a href="#">Accessibility</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-[#ffd200]">
                               <li><a href="#">Contact</a></li>
                              <li><a href="#">Maps</a></li>
                              <li><a href="#">Jobs</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1240px] mx-auto mt-16 text-center text-xs text-gray-600 border-t border-gray-800 pt-8">
                      © 2026 {schoolName} Regents
                  </div>
             </footer>
        </div>
    );
};

export default UCIrvine;
